'use client';

import { createClient } from '@/lib/supabase/client';

export interface ForgePlayerStats {
  forgeScore: number;
  currentStreak: number;
  longestStreak: number;
  fullName: string;
  email: string;
}

export interface ForgeComplianceStats {
  calorieCompliance: number;
  proteinCompliance: number;
  trainingCompliance: number;
  sleepCompliance: number;
  cleanEatingDays: number;
  totalDays: number;
}

export interface ForgeArenaData {
  theo: ForgePlayerStats | null;
  tracy: ForgePlayerStats | null;
  theoCompliance7d: ForgeComplianceStats;
  tracyCompliance7d: ForgeComplianceStats;
  weeklyScores: { day: string; theo: number; tracy: number }[];
  todayScores: { theo: number; tracy: number };
  weekScores: { theo: number; tracy: number };
  monthScores: { theo: number; tracy: number };
  monthlyCheckins: { date: string; score: number; userId: string }[];
}

function isSchemaError(error: any): boolean {
  if (!error) return false;
  if (error.code && typeof error.code === 'string') {
    const cls = error.code.substring(0, 2);
    if (cls === '42' || cls === '08') return true;
    if (cls === '23') return false;
  }
  if (error.message) {
    return /relation.*does not exist|column.*does not exist|syntax error/i.test(error.message);
  }
  return false;
}

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function avgScore(checkins: any[]): number {
  if (!checkins?.length) return 0;
  const total = checkins.reduce((s, c) => s + (c.forge_score_earned ?? 0), 0);
  return Math.round(total / checkins.length);
}

function calcCompliance(checkins: any[]): ForgeComplianceStats {
  const total = checkins?.length ?? 0;
  if (!total) {
    return { calorieCompliance: 0, proteinCompliance: 0, trainingCompliance: 0, sleepCompliance: 0, cleanEatingDays: 0, totalDays: 0 };
  }
  const nutrition = checkins.filter((c) => c.nutrition_compliant).length;
  const training = checkins.filter((c) => c.training_compliant).length;
  const sleep = checkins.filter((c) => c.sleep_compliant).length;
  return {
    calorieCompliance: Math.round((nutrition / total) * 100),
    proteinCompliance: Math.round((nutrition / total) * 100),
    trainingCompliance: Math.round((training / total) * 100),
    sleepCompliance: Math.round((sleep / total) * 100),
    cleanEatingDays: nutrition,
    totalDays: total,
  };
}

export const forgeArenaService = {
  async getAllUsersProfiles(): Promise<ForgePlayerStats[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('forge_user_profiles')
        .select('id, full_name, email, forge_score, current_streak, longest_streak')
        .order('forge_score', { ascending: false });

      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return (data || []).map((r) => ({
        forgeScore: r.forge_score ?? 0,
        currentStreak: r.current_streak ?? 0,
        longestStreak: r.longest_streak ?? 0,
        fullName: r.full_name ?? '',
        email: r.email ?? '',
      }));
    } catch (err: any) {
      console.log('forgeArenaService.getAllUsersProfiles error:', err.message);
      return [];
    }
  },

  async getArenaData(): Promise<ForgeArenaData> {
    const supabase = createClient();

    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];

    const day7Ago = new Date(today);
    day7Ago.setDate(today.getDate() - 6);
    const day7AgoStr = day7Ago.toISOString().split('T')[0];

    const day30Ago = new Date(today);
    day30Ago.setDate(today.getDate() - 29);
    const day30AgoStr = day30Ago.toISOString().split('T')[0];

    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    const monthStartStr = monthStart.toISOString().split('T')[0];

    const emptyCompliance: ForgeComplianceStats = {
      calorieCompliance: 0, proteinCompliance: 0, trainingCompliance: 0,
      sleepCompliance: 0, cleanEatingDays: 0, totalDays: 0,
    };

    const defaultResult: ForgeArenaData = {
      theo: null, tracy: null,
      theoCompliance7d: emptyCompliance, tracyCompliance7d: emptyCompliance,
      weeklyScores: [], todayScores: { theo: 0, tracy: 0 },
      weekScores: { theo: 0, tracy: 0 }, monthScores: { theo: 0, tracy: 0 },
      monthlyCheckins: [],
    };

    try {
      // Fetch all profiles
      const { data: profiles, error: profilesError } = await supabase
        .from('forge_user_profiles')
        .select('id, full_name, email, forge_score, current_streak, longest_streak');

      if (profilesError) {
        if (isSchemaError(profilesError)) throw profilesError;
        return defaultResult;
      }

      if (!profiles?.length) return defaultResult;

      // Identify Theo and Tracy by name (case-insensitive)
      const theoProfile = profiles.find((p) => p.full_name?.toLowerCase().includes('theo'));
      const tracyProfile = profiles.find((p) => p.full_name?.toLowerCase().includes('tracy'));

      const theoStats: ForgePlayerStats | null = theoProfile
        ? { forgeScore: theoProfile.forge_score ?? 0, currentStreak: theoProfile.current_streak ?? 0, longestStreak: theoProfile.longest_streak ?? 0, fullName: theoProfile.full_name, email: theoProfile.email }
        : null;

      const tracyStats: ForgePlayerStats | null = tracyProfile
        ? { forgeScore: tracyProfile.forge_score ?? 0, currentStreak: tracyProfile.current_streak ?? 0, longestStreak: tracyProfile.longest_streak ?? 0, fullName: tracyProfile.full_name, email: tracyProfile.email }
        : null;

      // Fetch 30-day checkins for both users
      const userIds = [theoProfile?.id, tracyProfile?.id].filter(Boolean);
      if (!userIds.length) return { ...defaultResult, theo: theoStats, tracy: tracyStats };

      const { data: checkins30d, error: checkinsError } = await supabase
        .from('forge_daily_checkins')
        .select('user_id, checkin_date, forge_score_earned, nutrition_compliant, training_compliant, sleep_compliant')
        .in('user_id', userIds)
        .gte('checkin_date', day30AgoStr)
        .lte('checkin_date', todayStr)
        .order('checkin_date', { ascending: true });

      if (checkinsError) {
        if (isSchemaError(checkinsError)) throw checkinsError;
        return { ...defaultResult, theo: theoStats, tracy: tracyStats };
      }

      const theoCheckins30d = (checkins30d || []).filter((c) => c.user_id === theoProfile?.id);
      const tracyCheckins30d = (checkins30d || []).filter((c) => c.user_id === tracyProfile?.id);

      const theoCheckins7d = theoCheckins30d.filter((c) => c.checkin_date >= day7AgoStr);
      const tracyCheckins7d = tracyCheckins30d.filter((c) => c.checkin_date >= day7AgoStr);

      // Today's scores
      const theoToday = theoCheckins30d.find((c) => c.checkin_date === todayStr);
      const tracyToday = tracyCheckins30d.find((c) => c.checkin_date === todayStr);

      // 7-day avg scores
      const theoWeekAvg = avgScore(theoCheckins7d);
      const tracyWeekAvg = avgScore(tracyCheckins7d);

      // Month avg scores
      const theoMonthCheckins = theoCheckins30d.filter((c) => c.checkin_date >= monthStartStr);
      const tracyMonthCheckins = tracyCheckins30d.filter((c) => c.checkin_date >= monthStartStr);
      const theoMonthAvg = avgScore(theoMonthCheckins);
      const tracyMonthAvg = avgScore(tracyMonthCheckins);

      // Build weekly chart data (last 7 days)
      const weeklyScores: { day: string; theo: number; tracy: number }[] = [];
      for (let i = 6; i >= 0; i--) {
        const d = new Date(today);
        d.setDate(today.getDate() - i);
        const dateStr = d.toISOString().split('T')[0];
        const dayLabel = DAYS[d.getDay()];
        const theoDay = theoCheckins30d.find((c) => c.checkin_date === dateStr);
        const tracyDay = tracyCheckins30d.find((c) => c.checkin_date === dateStr);
        weeklyScores.push({
          day: dayLabel,
          theo: theoDay?.forge_score_earned ?? 0,
          tracy: tracyDay?.forge_score_earned ?? 0,
        });
      }

      // Monthly checkins for calendar heatmap
      const monthlyCheckins = (checkins30d || []).map((c) => ({
        date: c.checkin_date,
        score: c.forge_score_earned ?? 0,
        userId: c.user_id,
      }));

      return {
        theo: theoStats,
        tracy: tracyStats,
        theoCompliance7d: calcCompliance(theoCheckins7d),
        tracyCompliance7d: calcCompliance(tracyCheckins7d),
        weeklyScores,
        todayScores: {
          theo: theoToday?.forge_score_earned ?? theoStats?.forgeScore ?? 0,
          tracy: tracyToday?.forge_score_earned ?? tracyStats?.forgeScore ?? 0,
        },
        weekScores: { theo: theoWeekAvg, tracy: tracyWeekAvg },
        monthScores: { theo: theoMonthAvg, tracy: tracyMonthAvg },
        monthlyCheckins,
      };
    } catch (err: any) {
      console.log('forgeArenaService.getArenaData error:', err.message);
      return defaultResult;
    }
  },
};
